/**
 * @fileoverview Mission panel — the 4C/ID learning-task interface.
 *
 * Shows the learning path (mission list with stars), and for the active
 * mission: brief, steps, live success criteria, diagnostic questions and
 * the pass banner.
 *
 * @module HydraulicBalancingTesting/components/MissionPanel
 */

import React, { useState } from 'react';
import { Button, Tag, Radio, Alert, Space, Popconfirm } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleOutlined,
  StarFilled,
  StarOutlined,
  ReloadOutlined,
  RightOutlined,
  BookOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

import styles from '../HydraulicBalancingTesting.module.css';

const Stars = ({ count, max = 3 }) => (
  <span className={styles.stars}>
    {Array.from({ length: max }).map((_, i) =>
      i < count ? <StarFilled key={i} style={{ color: '#faad14' }} /> : <StarOutlined key={i} style={{ color: '#d9d9d9' }} />
    )}
  </span>
);

const KIND_LABEL = {
  observe: 'Observe & diagnose',
  balance: 'Balance',
  optimize: 'Optimize',
  transfer: 'Transfer task',
};

// ----------------------------------------------------------------------------
// Mission list (learning path)
// ----------------------------------------------------------------------------

const MissionList = ({ missions, progress, onStart }) => (
  <div>
    <Alert
      type="info"
      showIcon
      icon={<BookOutlined />}
      message="Learning path"
      description="Five tasks from observation to a full service call — each one is a complete, realistic job with less and less help. You can also skip straight to the Sandbox via 'Free play'."
      style={{ marginBottom: 12 }}
    />
    {missions.map((m) => {
      const p = progress[m.id];
      return (
        <div key={m.id} className={styles.missionItem} onClick={() => onStart(m.id)}>
          <div className={styles.missionItemHeader}>
            <span>
              {p?.completed ? (
                <CheckCircleFilled style={{ color: '#52c41a', marginRight: 6 }} />
              ) : (
                <span className={styles.missionNumber}>{m.order}</span>
              )}
              <b>{m.title}</b>
            </span>
            <Stars count={p?.stars || 0} />
          </div>
          <div className={styles.missionItemSub}>
            <Tag>{KIND_LABEL[m.kind]}</Tag>
            <span className={styles.missionTagline}>{m.tagline}</span>
          </div>
        </div>
      );
    })}
  </div>
);

// ----------------------------------------------------------------------------
// Diagnostic question
// ----------------------------------------------------------------------------

const Question = ({ question, state, onAnswer }) => {
  const [choice, setChoice] = useState(null);
  const answered = state?.correct;
  const wrong = state && !state.correct && state.chosen !== undefined;

  return (
    <div className={styles.questionBlock}>
      <p className={styles.questionText}>{question.text}</p>
      <Radio.Group
        value={answered ? question.correct : choice}
        disabled={answered}
        onChange={(e) => setChoice(e.target.value)}
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
      >
        {question.options.map((opt, i) => (
          <Radio key={i} value={i}>{opt}</Radio>
        ))}
      </Radio.Group>
      {!answered && (
        <Button
          size="small"
          type="primary"
          style={{ marginTop: 8 }}
          disabled={choice === null}
          onClick={() => onAnswer(question.id, choice)}
        >
          Check answer
        </Button>
      )}
      {answered && (
        <Alert type="success" showIcon style={{ marginTop: 8 }} message="Correct!" description={question.explain} />
      )}
      {wrong && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message="Not quite — look at the overlays again and retry."
        />
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Active mission view
// ----------------------------------------------------------------------------

const ActiveMission = ({
  mission,
  evaluation,
  questionState,
  onAnswer,
  onRestart,
  onExit,
  onNext,
  hasNext,
  onOpenTheory,
}) => (
  <div>
    <div className={styles.missionHeaderRow}>
      <div>
        <Tag color="blue">Task {mission.order} / 5 · {KIND_LABEL[mission.kind]}</Tag>
        <p className={styles.missionTitle}>{mission.title}</p>
      </div>
      <Space>
        <Button size="small" icon={<ReloadOutlined />} onClick={onRestart}>Restart</Button>
        <Popconfirm title="Leave this task and switch to free play?" onConfirm={onExit}>
          <Button size="small" icon={<ExperimentOutlined />}>Sandbox</Button>
        </Popconfirm>
      </Space>
    </div>

    {mission.brief.map((p, i) => (
      <p key={i} className={styles.missionBrief}>{p}</p>
    ))}

    {mission.theoryRefs?.length > 0 && (
      <p className={styles.missionTheoryRefs}>
        <BookOutlined />{' '}
        Background:{' '}
        {mission.theoryRefs.map((ref, i) => (
          <React.Fragment key={ref}>
            {i > 0 && ' · '}
            <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => onOpenTheory(ref)}>
              {ref.replace(/-/g, ' ')}
            </Button>
          </React.Fragment>
        ))}
      </p>
    )}

    <p className={styles.sectionLabel}>How to proceed</p>
    <ol className={styles.stepsList}>
      {mission.steps.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ol>

    {mission.questions && (
      <>
        <p className={styles.sectionLabel}>Diagnostic questions</p>
        {mission.questions.map((q) => (
          <Question key={q.id} question={q} state={questionState[q.id]} onAnswer={onAnswer} />
        ))}
      </>
    )}

    <p className={styles.sectionLabel}>Success criteria (live)</p>
    <div className={styles.criteriaList}>
      {evaluation?.criteria.map((c, i) => (
        <div key={i} className={styles.criterionRow}>
          {c.pass ? (
            <CheckCircleFilled style={{ color: '#52c41a' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#bfbfbf' }} />
          )}
          <span className={c.pass ? styles.criterionPass : undefined}>{c.label}</span>
          <span className={styles.criterionDetail}>{c.detail}</span>
        </div>
      ))}
    </div>

    {evaluation?.passed && (
      <Alert
        type="success"
        showIcon
        style={{ marginTop: 12 }}
        message={
          <span>
            Task complete! <Stars count={evaluation.stars} />
          </span>
        }
        description={
          evaluation.stars === 3
            ? 'A flawless job — installer-grade work.'
            : 'Passed. Restart the task and tighten the tolerances to earn more stars.'
        }
        action={
          hasNext ? (
            <Button type="primary" icon={<RightOutlined />} onClick={onNext}>
              Next task
            </Button>
          ) : (
            <Button type="primary" icon={<ExperimentOutlined />} onClick={onExit}>
              Free play
            </Button>
          )
        }
      />
    )}
  </div>
);

// ----------------------------------------------------------------------------
// Panel
// ----------------------------------------------------------------------------

export const MissionPanel = ({ missionEngine, onOpenTheory }) => {
  const {
    missions,
    activeMission,
    progress,
    questionState,
    evaluation,
    startMission,
    restartMission,
    exitToSandbox,
    answerQuestion,
  } = missionEngine;

  if (!activeMission) {
    return (
      <div className={styles.panelCard}>
        <MissionList missions={missions} progress={progress} onStart={startMission} />
        <Alert
          type="warning"
          style={{ marginTop: 4 }}
          message="Free play (Sandbox)"
          description="No goals, everything unlocked — experiment freely with all 9 rooms, the pump and the heating curve."
          action={<Button onClick={exitToSandbox}>Start sandbox</Button>}
        />
      </div>
    );
  }

  const nextMission = missions.find((m) => m.order === activeMission.order + 1);

  return (
    <div className={styles.panelCard}>
      <ActiveMission
        mission={activeMission}
        evaluation={evaluation}
        questionState={questionState}
        onAnswer={answerQuestion}
        onRestart={restartMission}
        onExit={exitToSandbox}
        onNext={() => nextMission && startMission(nextMission.id)}
        hasNext={!!nextMission}
        onOpenTheory={onOpenTheory}
      />
    </div>
  );
};
